import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const FIELD_COLORS = {
  pest365: '#2563eb',
  pest365Plus: '#8b5cf6',
  oneTime: '#f59e0b',
  initialServiceVisit: '#16a34a',
};

const FIELD_LABELS = {
  pest365: 'Pest 365',
  pest365Plus: 'Pest 365 Plus',
  oneTime: 'One-Time',
  initialServiceVisit: 'Initial Service Visit',
};

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 24,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: '#111827',
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: '#6b7280',
    margin: '4px 0 16px 0',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 0',
    color: '#9ca3af',
    fontSize: 14,
  },
  legendContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    marginTop: 12,
    justifyContent: 'center',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    color: '#374151',
  },
  legendSwatch: {
    width: 12,
    height: 12,
    borderRadius: 3,
    flexShrink: 0,
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#1f2937',
    color: '#f9fafb',
    padding: '10px 14px',
    borderRadius: 6,
    fontSize: 12,
    lineHeight: 1.6,
    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
    zIndex: 9999,
    maxWidth: 240,
  },
  tooltipDate: {
    fontWeight: 700,
    marginBottom: 4,
    fontSize: 13,
    borderBottom: '1px solid #4b5563',
    paddingBottom: 4,
  },
  tooltipRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
  },
  tooltipDot: {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    marginRight: 6,
    verticalAlign: 'middle',
  },
};

export default function PriceEntropyChart({ data }) {
  const svgRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  const fieldKeys = useMemo(() => {
    if (!data || !data.fields) return [];
    return Object.keys(data.fields).filter((k) => FIELD_COLORS[k]);
  }, [data]);

  const hasData = useMemo(() => {
    return (
      data &&
      data.dates &&
      data.dates.length > 0 &&
      data.fields &&
      fieldKeys.length > 0
    );
  }, [data, fieldKeys]);

  useEffect(() => {
    if (!hasData || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 700;
    const height = 350;
    const margin = { top: 30, right: 24, bottom: 50, left: 52 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const parseDate = d3.timeParse('%Y-%m-%d');
    const dates = data.dates.map((d) => parseDate(d));

    const allEntropies = fieldKeys.flatMap((k) => data.fields[k].entropy);
    const maxEntropyValue = Math.max(
      ...fieldKeys.map((k) => data.fields[k].maxEntropy),
      ...allEntropies
    );

    const xScale = d3
      .scaleTime()
      .domain(d3.extent(dates))
      .range([0, innerWidth]);

    const yScale = d3
      .scaleLinear()
      .domain([0, maxEntropyValue * 1.1])
      .nice()
      .range([innerHeight, 0]);

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Grid lines
    g.append('g')
      .attr('class', 'grid-y')
      .selectAll('line')
      .data(yScale.ticks(6))
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', (d) => yScale(d))
      .attr('y2', (d) => yScale(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Max entropy dashed line
    const representativeMaxEntropy = data.fields[fieldKeys[0]].maxEntropy;
    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', yScale(representativeMaxEntropy))
      .attr('y2', yScale(representativeMaxEntropy))
      .attr('stroke', '#ef4444')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '6,4')
      .attr('opacity', 0.7);

    g.append('text')
      .attr('x', innerWidth - 4)
      .attr('y', yScale(representativeMaxEntropy) - 6)
      .attr('text-anchor', 'end')
      .attr('fill', '#ef4444')
      .attr('font-size', 10)
      .attr('font-weight', 600)
      .text(`Max entropy (${representativeMaxEntropy.toFixed(1)} bits)`);

    // Area + line for each field
    fieldKeys.forEach((fieldKey) => {
      const entropyValues = data.fields[fieldKey].entropy;
      const lineData = dates.map((d, i) => ({ date: d, value: entropyValues[i] }));

      const area = d3
        .area()
        .x((d) => xScale(d.date))
        .y0(innerHeight)
        .y1((d) => yScale(d.value))
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(lineData)
        .attr('d', area)
        .attr('fill', FIELD_COLORS[fieldKey])
        .attr('opacity', 0.08);

      const line = d3
        .line()
        .x((d) => xScale(d.date))
        .y((d) => yScale(d.value))
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(lineData)
        .attr('d', line)
        .attr('fill', 'none')
        .attr('stroke', FIELD_COLORS[fieldKey])
        .attr('stroke-width', 2)
        .attr('opacity', 0.9);
    });

    // Interpretation annotations
    g.append('text')
      .attr('x', 8)
      .attr('y', innerHeight - 8)
      .attr('fill', '#9ca3af')
      .attr('font-size', 10)
      .attr('font-style', 'italic')
      .text('Low entropy = concentrated pricing');

    g.append('text')
      .attr('x', 8)
      .attr('y', 16)
      .attr('fill', '#9ca3af')
      .attr('font-size', 10)
      .attr('font-style', 'italic')
      .text('High entropy = diverse pricing');

    // X axis
    const xAxis = g
      .append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(
        d3
          .axisBottom(xScale)
          .ticks(Math.min(dates.length, 8))
          .tickFormat(d3.timeFormat('%b %d'))
      );

    xAxis.select('.domain').attr('stroke', '#d1d5db');
    xAxis.selectAll('.tick line').attr('stroke', '#d1d5db');
    xAxis.selectAll('.tick text').attr('fill', '#6b7280').attr('font-size', 10);

    // Y axis
    const yAxis = g.append('g').call(
      d3.axisLeft(yScale).ticks(6).tickFormat((d) => `${d.toFixed(1)}`)
    );

    yAxis.select('.domain').attr('stroke', '#d1d5db');
    yAxis.selectAll('.tick line').attr('stroke', '#d1d5db');
    yAxis.selectAll('.tick text').attr('fill', '#6b7280').attr('font-size', 10);

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerHeight / 2)
      .attr('y', -40)
      .attr('text-anchor', 'middle')
      .attr('fill', '#6b7280')
      .attr('font-size', 11)
      .text('Entropy (bits)');

    // Invisible overlay for tooltip hover
    const bisector = d3.bisector((d) => d).left;

    g.append('rect')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('fill', 'transparent')
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event);
        const hoveredDate = xScale.invert(mx);
        const idx = bisector(dates, hoveredDate);
        const clampedIdx = Math.max(0, Math.min(idx, dates.length - 1));

        // Snap to closest point
        let snapIdx = clampedIdx;
        if (clampedIdx > 0) {
          const d0 = dates[clampedIdx - 1];
          const d1 = dates[clampedIdx];
          if (d1 && hoveredDate - d0 > d1 - hoveredDate) {
            snapIdx = clampedIdx;
          } else {
            snapIdx = clampedIdx - 1;
          }
        }

        const dateStr = data.dates[snapIdx];
        const values = {};
        fieldKeys.forEach((k) => {
          values[k] = data.fields[k].entropy[snapIdx];
        });

        setTooltip({
          x: event.clientX + 14,
          y: event.clientY - 10,
          date: dateStr,
          values,
        });
      })
      .on('mouseleave', () => {
        setTooltip(null);
      });
  }, [data, hasData, fieldKeys]);

  if (!hasData) {
    return (
      <div style={styles.card}>
        <h3 style={styles.title}>Price Entropy Analysis</h3>
        <p style={styles.subtitle}>
          Shannon entropy measuring pricing diversity over time
        </p>
        <div style={styles.emptyState}>No entropy data available</div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <h3 style={styles.title}>Price Entropy Analysis</h3>
      <p style={styles.subtitle}>
        Shannon entropy measuring pricing diversity over time
      </p>

      <svg
        ref={svgRef}
        viewBox="0 0 700 350"
        width="100%"
        style={{ display: 'block' }}
      />

      <div style={styles.legendContainer}>
        {fieldKeys.map((k) => (
          <div key={k} style={styles.legendItem}>
            <span
              style={{
                ...styles.legendSwatch,
                backgroundColor: FIELD_COLORS[k],
              }}
            />
            {FIELD_LABELS[k] || k}
          </div>
        ))}
        <div style={styles.legendItem}>
          <span
            style={{
              width: 16,
              height: 0,
              borderTop: '2px dashed #ef4444',
              flexShrink: 0,
              alignSelf: 'center',
            }}
          />
          Max Entropy
        </div>
      </div>

      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={styles.tooltipDate}>{tooltip.date}</div>
          {fieldKeys.map((k) => (
            <div key={k} style={styles.tooltipRow}>
              <span>
                <span
                  style={{
                    ...styles.tooltipDot,
                    backgroundColor: FIELD_COLORS[k],
                  }}
                />
                {FIELD_LABELS[k] || k}
              </span>
              <span style={{ fontWeight: 600 }}>
                {tooltip.values[k] != null
                  ? tooltip.values[k].toFixed(2)
                  : 'N/A'}{' '}
                bits
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
